/**
 * Saved Assessment Report Page
 *
 * View and manage a saved assessment report (beoordelingsverslag).
 * Supports status management, WYSIWYG editing, and AI-powered editing.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type AssessmentReportStatus } from '../../lib/evaluationsApi';
import { useAssessmentReport, useUpdateAssessmentReportStatus, useUpdateAssessmentReport } from '../team/useAssessmentReports';
import { useAuth } from '../auth/AuthContext';
import { ReportEditor } from './ReportEditor';
import { ReportAiChat } from './ReportAiChat';
import { ReportBlockView } from './ReportBlockView';
import { type SectionType, moveBlockInHtml, deleteBlockInHtml } from './reportHtmlUtils';

const STATUS_CONFIG: Record<AssessmentReportStatus, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  ingediend: { label: 'Ingediend', color: 'text-blue-800', bgColor: 'bg-blue-100' },
  definitief: { label: 'Definitief', color: 'text-green-800', bgColor: 'bg-green-100' },
};

const FINAL_ASSESSMENT_OPTIONS: { value: string; label: string }[] = [
  { value: 'onvoldoende', label: 'Onvoldoende (0%)' },
  { value: 'onder_verwachting', label: 'Onder verwachting (50%)' },
  { value: 'naar_verwachting', label: 'Naar verwachting (100%)' },
  { value: 'boven_verwachting', label: 'Boven verwachting (125%)' },
];

function getFinalAssessmentLabel(value: string | null): string {
  if (!value) return '';
  return FINAL_ASSESSMENT_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function injectFinalAssessmentHtml(html: string, finalAssessment: string | null): string {
  if (!finalAssessment) return html;
  const label = getFinalAssessmentLabel(finalAssessment);
  const section = `
      <!-- Eindbeoordeling -->
      <div class="section">
        <div class="section-header">
          <div class="section-icon conclusion">&#9733;</div>
          <h2>Eindbeoordeling</h2>
        </div>
        <div class="conclusion-box" style="text-align: center; font-size: 18px;">
          <strong>${label}</strong>
        </div>
      </div>`;

  // Insert before signature-section (legacy reports)
  const sigIdx = html.indexOf('<div class="signature-section">');
  if (sigIdx !== -1) {
    return html.slice(0, sigIdx) + section + '\n      ' + html.slice(sigIdx);
  }
  // Insert before footer (legacy reports)
  const footerIdx = html.indexOf('<div class="footer">');
  if (footerIdx !== -1) {
    return html.slice(0, footerIdx) + section + '\n    ' + html.slice(footerIdx);
  }
  // Insert at end of .content div, before </div></div></body>
  const closingMatch = html.match(/<\/div>\s*<\/div>\s*<\/body>/i);
  if (closingMatch?.index !== undefined) {
    return html.slice(0, closingMatch.index) + section + '\n    ' + html.slice(closingMatch.index);
  }
  return html;
}

export function SavedAssessmentReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const reportId = Number(id);

  const { data: reportData, isLoading, error } = useAssessmentReport(reportId);
  const updateStatus = useUpdateAssessmentReportStatus();
  const updateReport = useUpdateAssessmentReport();

  const [showStatusSelect, setShowStatusSelect] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  // AI modified HTML (pending, not yet saved)
  const [aiModifiedHtml, setAiModifiedHtml] = useState<string | null>(null);

  // AI chat panel open state
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);

  const report = reportData?.data;
  const isDefinitief = report?.status === 'definitief';
  const isLocked = isDefinitief && !hasRole('admin');

  // Set document title for PDF filename: "Beoordelingsverslag {name} {year}"
  useEffect(() => {
    if (!report) return;
    const year = report.periodeStart ? new Date(report.periodeStart).getFullYear() : '';
    const name = report.employeeName || '';
    document.title = `Beoordelingsverslag ${name} ${year}`.trim();
    return () => { document.title = 'HR Portal'; };
  }, [report]);

  // The HTML to display: AI-modified version takes precedence over saved version
  const displayHtml = aiModifiedHtml ?? report?.htmlContent ?? '';

  // Inject eindbeoordeling section into display HTML
  const displayHtmlWithAssessment = useMemo(
    () => injectFinalAssessmentHtml(displayHtml, report?.finalAssessment ?? null),
    [displayHtml, report?.finalAssessment]
  );

  // Handle status change
  const handleStatusChange = async (newStatus: AssessmentReportStatus) => {
    if (!report || isLocked) return;

    try {
      await updateStatus.mutateAsync({ id: report.id, status: newStatus });
      setShowStatusSelect(false);

      if (newStatus === 'definitief') {
        setSaveMessage({ type: 'success', text: hasRole('admin') ? 'Verslag definitief gemaakt.' : 'Verslag definitief gemaakt. Kan niet meer worden gewijzigd.' });
      } else {
        setSaveMessage({ type: 'success', text: `Status gewijzigd naar ${STATUS_CONFIG[newStatus].label}` });
      }

      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update status:', err);
      setSaveMessage({ type: 'error', text: 'Status wijzigen mislukt.' });
    }
  };

  // Handle final assessment change
  const handleFinalAssessmentChange = async (value: string) => {
    if (!report || isLocked) return;
    const newValue = value === '' ? null : value;
    try {
      await updateReport.mutateAsync({
        id: report.id,
        input: { finalAssessment: newValue },
      });
      setSaveMessage({ type: 'success', text: 'Eindbeoordeling opgeslagen.' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update final assessment:', err);
      setSaveMessage({ type: 'error', text: 'Eindbeoordeling opslaan mislukt.' });
    }
  };

  // Enter edit mode - clear any pending AI edit
  const handleStartEdit = () => {
    if (!report || isLocked) return;
    setAiModifiedHtml(null);
    setIsEditMode(true);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // Save edited content (from WYSIWYG editor or AI edit)
  const handleSaveEdit = async (html: string) => {
    if (!report || isLocked) return;

    try {
      await updateReport.mutateAsync({
        id: report.id,
        input: { htmlContent: html },
      });
      setIsEditMode(false);
      setAiModifiedHtml(null);
      setSaveMessage({ type: 'success', text: 'Verslag opgeslagen.' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save report:', err);
      setSaveMessage({ type: 'error', text: 'Opslaan mislukt.' });
    }
  };

  // AI chat: apply edit (preview, not saved yet)
  const handleAiApplyEdit = (html: string) => {
    setAiModifiedHtml(html);
  };

  // AI chat: save the pending AI edit
  const handleAiSaveEdit = (html: string) => {
    handleSaveEdit(html);
  };

  // AI chat: revert pending AI edit
  const handleAiRevert = () => {
    setAiModifiedHtml(null);
  };

  // Block delete: visually remove a block (pending until saved)
  const handleDeleteBlock = useCallback((sectionType: SectionType, blockIndex: number) => {
    const newHtml = deleteBlockInHtml(displayHtml, sectionType, blockIndex);
    if (newHtml !== displayHtml) {
      setAiModifiedHtml(newHtml);
    }
  }, [displayHtml]);

  // Block move: reorder blocks within or between sections
  const handleMoveBlock = useCallback((
    fromSection: SectionType,
    fromIndex: number,
    toSection: SectionType,
    toIndex: number,
  ) => {
    const newHtml = moveBlockInHtml(displayHtml, fromSection, fromIndex, toSection, toIndex);
    if (newHtml !== displayHtml) {
      handleSaveEdit(newHtml);
    }
  }, [displayHtml, handleSaveEdit]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Beoordelingsverslag Laden</h1>
          <p className="text-gray-600">Even geduld...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verslag Niet Gevonden</h1>
          <p className="text-gray-600 mb-6">Het beoordelingsverslag kon niet worden geladen.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Terug
          </button>
        </div>
      </div>
    );
  }

  // WYSIWYG edit mode view
  if (isEditMode) {
    return (
      <ReportEditor
        htmlContent={report.htmlContent}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={updateReport.isPending}
      />
    );
  }

  return (
    <>
      {/* Print Controls (hidden when printing) */}
      <div className={`no-print fixed top-4 flex flex-col gap-2 z-50 transition-all duration-300 ${isAiChatOpen ? 'right-[416px]' : 'right-4'}`}>
        {/* Status Badge and Save Status */}
        <div className="flex items-center gap-2 justify-end">
          {saveMessage && (
            <span
              className={`px-3 py-1 rounded-lg text-sm shadow-lg ${
                saveMessage.type === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {saveMessage.text}
            </span>
          )}
          <div className="relative">
            <button
              onClick={() => !isLocked && setShowStatusSelect(!showStatusSelect)}
              disabled={isLocked}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
                STATUS_CONFIG[report.status].bgColor
              } ${STATUS_CONFIG[report.status].color} ${
                isLocked ? 'cursor-not-allowed' : 'hover:opacity-90'
              }`}
            >
              {isLocked && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {STATUS_CONFIG[report.status].label}
              {!isLocked && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
            {showStatusSelect && !isLocked && (
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                {(['concept', 'ingediend', 'definitief'] as AssessmentReportStatus[]).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={updateStatus.isPending}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                      status === report.status ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    {STATUS_CONFIG[status].label}
                    {status === 'definitief' && (
                      <span className="text-xs text-gray-500 ml-1">(vergrendelt)</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Eindbeoordeling Dropdown */}
        {!isLocked && (
          <div className="flex items-center gap-2 justify-end">
            <label className="text-sm font-medium text-gray-700 bg-white px-2 py-1 rounded shadow-lg">Eindbeoordeling</label>
            <select
              value={report.finalAssessment ?? ''}
              onChange={(e) => handleFinalAssessmentChange(e.target.value)}
              disabled={updateReport.isPending}
              className="px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg border border-gray-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">-- Geen --</option>
              {FINAL_ASSESSMENT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {!isLocked && (
            <button
              onClick={handleStartEdit}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Bewerken
            </button>
          )}
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Afdrukken / PDF
          </button>
          <button
            onClick={() => navigate(`/team?employeeId=${report.employeeId}&tab=assessment-reports`)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 shadow-lg"
          >
            Sluiten
          </button>
        </div>
      </div>

      {/* Pending AI edit indicator */}
      {aiModifiedHtml && (
        <div className={`no-print fixed bottom-4 z-50 bg-amber-50 border border-amber-300 rounded-lg shadow-xl px-4 py-2 flex items-center gap-3 transition-all duration-300 ${isAiChatOpen ? 'right-[416px]' : 'left-1/2 transform -translate-x-1/2'}`}>
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm text-amber-800 font-medium">Wijziging niet opgeslagen</span>
          <button
            onClick={handleAiRevert}
            className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Ongedaan maken
          </button>
          <button
            onClick={() => handleAiSaveEdit(aiModifiedHtml)}
            disabled={updateReport.isPending}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateReport.isPending ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      )}

      {/* Render HTML Content */}
      {!isLocked ? (
        <ReportBlockView
          html={displayHtmlWithAssessment}
          onMoveBlock={handleMoveBlock}
          onDeleteBlock={handleDeleteBlock}
          isAiChatOpen={isAiChatOpen}
        />
      ) : (
        <div
          dangerouslySetInnerHTML={{ __html: displayHtmlWithAssessment }}
          className={`assessment-report-content transition-all duration-300 ${isAiChatOpen ? 'mr-[400px]' : ''}`}
        />
      )}

      {/* AI Chat */}
      <ReportAiChat
        reportId={report.id}
        isLocked={isLocked}
        currentHtml={displayHtml}
        onApplyEdit={handleAiApplyEdit}
        onSaveEdit={handleAiSaveEdit}
        onRevert={handleAiRevert}
        hasPendingEdit={!!aiModifiedHtml}
        isSaving={updateReport.isPending}
        onOpenChange={setIsAiChatOpen}
      />

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          /* Allow sections to break across pages, keep headers with content */
          .section { break-inside: auto !important; page-break-inside: auto !important; }
          .section-header { break-after: avoid !important; page-break-after: avoid !important; }
          .summary { break-after: avoid !important; page-break-after: avoid !important; }
          .item-card { break-inside: avoid !important; page-break-inside: avoid !important; }
          .agreement-card { break-inside: avoid !important; page-break-inside: avoid !important; }
          .conclusion-box { break-inside: avoid !important; page-break-inside: avoid !important; }
        }
        .assessment-report-content {
          /* Container for the injected HTML */
        }
        /* Hide score badges in saved reports */
        .assessment-report-content .score-badge {
          display: none !important;
        }
        /* Hide signature section and footer (not used) */
        .signature-section, .footer {
          display: none !important;
        }
      `}</style>
    </>
  );
}
